import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { requireAdminRoute } from "@/lib/require-admin-route";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { apiFetch, ApiRequestError } from "@/lib/api-client";
import { humanizeApiError } from "@/lib/form-errors";
import { useToast } from "@/components/ui/Toast";
import { useTopbar } from "@/components/chrome/use-topbar";
import type { ScheduleConfigInput } from "@/lib/settings-schemas";

// The schedule API may return `recipients` as a one-release alias for
// `mailerRecipients` (F-09-05). Keep working when only the latter exists.
type ScheduleConfigResponse = ScheduleConfigInput & { recipients?: string };

const DEFAULTS: ScheduleConfigInput = {
  enabled: false,
  cronExpr: "0 13 * * *",
  lookbackDays: 60,
  mailerRecipients: "",
};

export const Route = createFileRoute("/_app/settings/schedule")({
  beforeLoad: requireAdminRoute,
  component: SchedulePage,
});

function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

// Tiny humanizer for 5-field standard cron. Handles common daily patterns
// only; anything else returns the "custom" sentinel. MT offset is fixed at
// -7 (treat as MDT for the Denver-based team).
function humanizeCron(expr: string): string {
  const trimmed = expr.trim();
  const parts = trimmed.split(/\s+/);
  const custom = "Custom schedule (won't be humanized).";
  if (parts.length !== 5) return custom;
  const [m, h, dom, mon, dow] = parts;
  if (dom !== "*" || mon !== "*" || dow !== "*") return custom;
  const minute = Number(m);
  const hour = Number(h);
  if (!Number.isInteger(minute) || !Number.isInteger(hour)) return custom;
  if (minute < 0 || minute > 59 || hour < 0 || hour > 23) return custom;
  const mt = (hour - 7 + 24) % 24;
  return `Daily at ${pad2(hour)}:${pad2(minute)} UTC (${pad2(mt)}:${pad2(minute)} MT)`;
}

function recipientsToTextarea(csv: string): string {
  return csv
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .join("\n");
}

function recipientsToCsv(text: string): string {
  return text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean)
    .join(",");
}

function SchedulePage() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data, isLoading, error } = useQuery<ScheduleConfigResponse | null>({
    queryKey: ["settings", "schedule"],
    queryFn: () => apiFetch<ScheduleConfigResponse | null>("/api/settings/schedule"),
  });

  const [form, setForm] = useState<ScheduleConfigInput>(DEFAULTS);
  const [recipientsText, setRecipientsText] = useState("");
  const [seeded, setSeeded] = useState(false);

  const { setTopbar } = useTopbar();
  useEffect(() => {
    setTopbar({
      crumbs: [
        { label: "Operations" },
        { label: "Settings", to: "/settings" },
        { label: "Schedule" },
      ],
      rightSlot: null,
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar]);

  useEffect(() => {
    if (seeded) return;
    if (isLoading) return;
    const initial = data ?? DEFAULTS;
    setForm(initial);
    // Prefer the `recipients` alias when the API provides it (F-09-05).
    setRecipientsText(recipientsToTextarea(data?.recipients ?? initial.mailerRecipients));
    setSeeded(true);
  }, [data, isLoading, seeded]);

  const save = useMutation({
    mutationFn: (payload: ScheduleConfigInput) =>
      apiFetch<{ ok: true }>("/api/settings/schedule", {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      toast.success("Schedule saved.");
      void qc.invalidateQueries({ queryKey: ["settings", "schedule"] });
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.status === 401) {
        void navigate({ to: `/login?redirect=${encodeURIComponent(pathname)}` });
        return;
      }
      toast.error(humanizeApiError(err).summary);
    },
  });

  const saveError = save.isError ? humanizeApiError(save.error) : null;

  const cronHuman = useMemo(() => humanizeCron(form.cronExpr), [form.cronExpr]);

  if (isLoading) return <p>Loading…</p>;
  if (error) return <p className="text-red-400">Failed to load schedule.</p>;

  const rowClass =
    "flex items-center justify-between gap-4 rounded border border-rule-strong bg-surface px-4 py-3";

  return (
    <div className="space-y-6">
      <div className="border-b border-rule pb-5">
        <h1 className="mb-1 text-3xl font-semibold tracking-tight text-ink">Schedule</h1>
        <p className="text-sm text-muted">
          Control candidate discovery cadence, lookback window, and notification delivery.
        </p>
      </div>

      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ ...form, mailerRecipients: recipientsToCsv(recipientsText) });
        }}
      >
        <div className={rowClass}>
          <div>
            <div className="text-sm font-medium">Enabled</div>
            <div className="text-xs text-muted">
              When off, the scheduled scan is skipped on every run.
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.enabled}
            onClick={() => setForm((f) => ({ ...f, enabled: !f.enabled }))}
            className={`relative h-6 w-11 rounded-full transition-colors ${
              form.enabled ? "bg-gold" : "bg-paper-3"
            }`}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-ink transition-transform ${
                form.enabled ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>

        <div className="rounded border border-rule-strong bg-surface px-4 py-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4">
            <div>
              <div className="text-sm font-medium">Cron expression</div>
              <div className="text-xs text-muted">5-field standard cron, evaluated in UTC.</div>
            </div>
            <input
              type="text"
              value={form.cronExpr}
              onChange={(e) => setForm((f) => ({ ...f, cronExpr: e.target.value }))}
              className="w-full rounded border border-rule-strong bg-surface px-3 py-1.5 font-mono text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold md:w-44 md:text-right"
            />
          </div>
          <div className="mt-2 text-xs text-muted">{cronHuman}</div>
        </div>

        <div className="rounded border border-rule-strong bg-surface px-4 py-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4">
            <div>
              <div className="text-sm font-medium">Lookback window (days)</div>
              <div className="text-xs text-muted">
                How far back to scan for new loan recordings on each run.
              </div>
            </div>
            <input
              type="number"
              min={7}
              max={180}
              value={form.lookbackDays}
              onChange={(e) =>
                setForm((f) => ({ ...f, lookbackDays: clamp(Number(e.target.value), 7, 180) }))
              }
              className="w-full rounded border border-rule-strong bg-surface px-3 py-1.5 text-right text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold md:w-44"
            />
          </div>
        </div>

        <div className="rounded border border-rule-strong bg-surface px-4 py-3">
          <div className="text-sm font-medium">Mailer recipients (one per line)</div>
          <div className="text-xs text-muted">Email addresses that receive the daily mailer.</div>
          <textarea
            rows={4}
            value={recipientsText}
            onChange={(e) => setRecipientsText(e.target.value)}
            className="mt-2 w-full rounded border border-rule-strong bg-surface px-3 py-1.5 font-mono text-sm text-ink placeholder:text-muted-2 focus:outline-none focus:ring-1 focus:ring-gold"
          />
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          {saveError ? <span className="text-xs text-neg">{saveError.summary}</span> : null}
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded bg-gold px-4 py-2 text-sm font-semibold text-ink disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
